"use strict";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { AG_GRID_LOCALE_VN } from "@ag-grid-community/locale";
import {
  Box,
  Button,
  Flex,
  Grid,
  Group,
  Input,
  Menu,
  MultiSelect,
  rem,
  ScrollArea,
  Text,
  TextInput,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import {
  IconBarcode,
  IconBuildingSkyscraper,
  IconBuildingWarehouse,
  IconCheck,
  IconFileExport,
  IconSearch,
  IconTimeDuration15,
  IconX,
} from "@tabler/icons-react";
import { ColDef, GridReadyEvent, ModuleRegistry } from "ag-grid-community";
import {
  IServerSideDatasource,
  IServerSideGetRowsRequest,
  ServerSideRowModelModule,
} from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import dayjs from "dayjs";
import {
  paginationPageSize,
  paginationPageSizeSelector,
  repositoryDelivery,
  repositoryMdm,
  repositoryPos,
} from "../../../_base/_const/_constVar";
import BreadcrumbLayout1 from "../../../_base/component/_layout/_breadcrumb";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import LoadingOverlayCustom from "../../../common/LoadingOverLayCustom/LoadingOverLayCustom";
import { BASE_API_MDM } from "../../../config";
import { MessageResponse } from "../../../model/MessageResponse";

ModuleRegistry.registerModules([ServerSideRowModelModule]);
export interface TheKPhgModel {
  maChiNhanh: string;
  maKho: string;
  maVatTu: string;
  tenVatTu: string;
  donViTinh: string;
  serial: string;
  soChungTuNhap: string;
  ngayNhap: string; // format "DD/MM/YYYY"
  ngayHetHan: string; // format "DD/MM/YYYY"
  maNhaCungCap: string;
  tenNhaCungCap: string;
}

const GetTHEKPHG = () => {
  const [loading, setLoading] = useState(true);
  const gridRef = useRef<AgGridReact<any>>(null);
  const headerRef = React.useRef<HTMLDivElement>(null);
  const datasourceRef = useRef<IServerSideDatasource | null>(null);
  const [height, setHeight] = useState(800);
  const [dataBranchCode, setDataBranchCode] = useState<any[]>([]);
  const [dataInvCode, setDataInvCode] = useState<any[]>([]);

  const [dataItem, setDataItem] = useState<any[]>([]);
  const [searchItem, setSearchItem] = useState("");
  const [debouncedItemCode] = useDebouncedValue(searchItem, 500);
  const [visible, { toggle, close, open }] = useDisclosure(false);

  // Xoá useState search, chỉ dùng form
  const containerStyle = useMemo(
    () => ({ width: "100%", height: height }),
    [height],
  );
  const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
  const sizeGrid = { base: 12, sm: 6, md: 6, lg: 3 };

  useEffect(() => {
    const updateHeight = () => {
      const headerHeight = headerRef.current?.offsetHeight || 0;
      // 400 là tổng khoảng cách cần trừ (breadcrumb, filters, padding, pagination)
      const newHeight = window.innerHeight - (210 + headerHeight);
      setHeight(newHeight);
    };

    // Đợi DOM render xong rồi mới tính
    setTimeout(updateHeight, 0);
    window.addEventListener("resize", updateHeight);

    return () => {
      window.removeEventListener("resize", updateHeight);
    };
  }, []);

  // Chỗ này để làm lọc dùng form cho đỡ lag so với state
  const form = useForm<{
    keySearch: string;
    MaChiNhanh: string[];
    MaKho: string[];
    MaVatTu: string[];
    TuNgay: string | null;
    DenNgay: string | null;
    VatTuCode: string | null;
  }>({
    initialValues: {
      keySearch: "",
      MaChiNhanh: [],
      MaKho: [],
      MaVatTu: [],
      TuNgay: dayjs().startOf("month").format("YYYY-MM-DD"),
      DenNgay: dayjs().endOf("month").format("YYYY-MM-DD"),
      VatTuCode: null,
    },
  });
  const fetchData = async (request: IServerSideGetRowsRequest) => {
    try {
      const currentPageSize =
        gridRef.current?.api?.paginationGetPageSize() || paginationPageSize;
      const skip = request.startRow || 0;
      const take = currentPageSize;
      let url = `/api/v1/BcThoiHanMaThekPhieuBhg/get-list?isAll=true&Skip=${skip}&Take=${take}`;
      const values = form.getValues();
      if (values.MaChiNhanh && values.MaChiNhanh.length > 0) {
        values.MaChiNhanh.forEach((branchCode: string) => {
          url += `&MaChiNhanh=${branchCode}`;
        });
      }
      if (values.MaKho && values.MaKho.length > 0) {
        values.MaKho.forEach((invCode: string) => {
          url += `&MaKho=${invCode}`;
        });
      }
      if (values.VatTuCode) {
        values.VatTuCode.split(";")
          .map((s) => s.trim())
          .filter(Boolean)
          .forEach((maVatTu: string) => {
            url += `&MaVatTu=${maVatTu}`;
          });
      }
      if (values.TuNgay) {
        url += `&TuNgay=${values.TuNgay}`;
      }
      if (values.DenNgay) {
        url += `&DenNgay=${values.DenNgay}`;
      }
      if (values.keySearch) {
        url += `&KeySearch=${values.keySearch.trim()}`;
      }
      return await repositoryMdm.get<MessageResponse<TheKPhgModel[]>>(url);
    } catch (error) {
      console.error(error);
    }
  };

  // Call api bên server
  const getServerSideDatasource = useCallback((): IServerSideDatasource => {
    return {
      getRows: async (params) => {
        var response = await fetchData(params.request);
        // adding delay to simulate real server call
        if (response && response.success) {
          // Tách row TOTAL ra khỏi data chính
          const allData = response.data || [];
          const total = (response as any)?.totalCount ?? allData.length;
          params.success({ rowData: allData, rowCount: total });
          setLoading(false);
        } else {
          // inform the grid request failed
          params.fail();
          setLoading(false);
        }
      },
    };
  }, []);
  // Sau khi chọn tìm kiếm xong thì sẽ thực hiện click để call api này
  // const handleSearch = useCallback(() => {
  //     setLoading(true);
  //     const api = gridRef.current?.api;
  //     if (!api) {
  //         console.log("API not ready");
  //         return;
  //     }

  //     // Refresh server-side cache để gọi lại getRows với filter mới
  //     (api as any).refreshServerSide({ purge: true });
  // }, []);
  const handleSearch = useCallback(() => {
    // Xóa lỗi cũ
    form.setErrors({});

    const values = form.getValues();
    // Validate required TuNgay & DenNgay
    if (!values.TuNgay || !values.DenNgay) {
      if (!values.TuNgay) {
        form.setFieldError("TuNgay", "Vui lòng chọn ngày bắt đầu");
      }
      if (!values.DenNgay) {
        form.setFieldError("DenNgay", "Vui lòng chọn ngày kết thúc");
      }
      return;
    }
    // Validate TuNgay <= DenNgay
    if (dayjs(values.TuNgay).isAfter(dayjs(values.DenNgay))) {
      form.setFieldError(
        "TuNgay",
        "Ngày bắt đầu phải nhỏ hơn hoặc bằng ngày kết thúc",
      );
      return;
    }

    setLoading(true);
    const api = gridRef.current?.api;
    if (!api) {
      console.log("API not ready");
      return;
    }

    // Refresh server-side cache để gọi lại getRows với filter mới
    (api as any).refreshServerSide({ purge: true });
  }, [form]);

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      // đăng datasource đúng để ag-Grid gọi getRows
      const datasource = getServerSideDatasource();
      datasourceRef.current = datasource;
      params.api.setGridOption("serverSideDatasource", datasource);
      console.log("Grid ready, datasource registered");
    },
    [getServerSideDatasource],
  );

  const getRowId = useCallback((params: any) => {
    const d = params.data || {};
    const id =
      `${d.maVatTu ?? ""}_${d.serial ?? ""}_${d.soChungTuNhap ?? ""}`.replace(
        /(^_+|_+$)/g,
        "",
      );
    // debug: console.log('[GetTHEKPHG] rowId=', id, params.data);
    return id || "";
  }, []);

  const getRowStyle = useCallback((params: any) => {
    if (params.node.rowPinned) {
      return {
        backgroundColor: "#e7f5ff",
        fontWeight: 700,
        fontSize: "14px",
        borderTop: "3px solid #15aabf",
      };
    }
    return undefined;
  }, []);

  // Hàm sửa lý sự kiện sắp xếp
  const handleSortChanged = useCallback(() => {
    const columnState = gridRef.current?.api?.getColumnState() || [];
    // tìm cột có sort (asc/desc)
    const sortedCol = columnState.find((c: any) => c.sort);
    if (sortedCol) {
      form.setFieldValue("SortBy", sortedCol.colId);
      form.setFieldValue(
        "SortDirection",
        (sortedCol.sort || "").toUpperCase() === "ASC" ? "ASC" : "DESC",
      );
    } else {
      form.setFieldValue("SortBy", null);
      form.setFieldValue("SortDirection", null);
    }
    // reload server-side data với sort mới
    gridRef.current?.api?.refreshServerSide?.({ purge: true });
  }, [form]);

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        field: "maChiNhanh",
        headerName: "Mã chi nhánh",
      },
      { field: "maKho", headerName: "Mã Kho" },
      {
        field: "tenViTri",
        headerName: "Vị trí kho",
      },
      {
        field: "maVatTu",
        headerName: "Mã vật tư",
      },
      { field: "tenVatTu", headerName: "Tên vật tư" },
      { field: "donViTinh", headerName: "Đơn vị tính" },
      { field: "serial", headerName: "Serial" },
      { field: "soChungTuNhap", headerName: "Số chứng từ nhập" },
      { field: "ngayNhap", headerName: "Ngày chứng từ" },
      { field: "ngayHetHan", headerName: "Ngày hết hạn" },
      { field: "maNhaCungCap", headerName: "Mã nhà cung cấp" },
      { field: "tenNhaCungCap", headerName: "Tên nhà cung cấp" },
    ],
    [form.getValues()],
  );

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      sortable: true,
      resizable: true,
    };
  }, []);

  // Lấy dữ liệu mã kho theo chi nhánh đã chọn
  const DataInvCode = async () => {
    const branchSearch =
      dataBranchCode.length > 0 && form.getValues()?.MaChiNhanh?.length > 0
        ? form
            .getValues()
            ?.MaChiNhanh.map((branchCode: string) => {
              const branch = dataBranchCode.find((b) => b.value === branchCode);
              return branch ? `&BranchId=${branch.id}` : "";
            })
            .join("")
        : "";

    const take = form.getValues()?.MaChiNhanh?.length * 50;

    const res = await repositoryDelivery.get<MessageResponse<any>>(
      `/api/v1/TblDmInventory/get-list-by-branchs?Skip=0&Take=${
        form.getValues()?.MaChiNhanh && form.getValues()?.MaChiNhanh?.length > 0
          ? take
          : 300
      }${branchSearch}`,
    );

    if (res && res.success) {
      const result = res.data;
      const data = result
        ?.filter((item: any) => item.idBranch && item.name)
        ?.map((item: any) => ({
          label: `${item.code} - ${item.name}`,
          value: item.code?.toString(),
        }));
      setDataInvCode(data);
    }
  };

  // Lấy dữ liệu mã chi nhánh
  const DataBrachCode = async () => {
    try {
      const response = await repositoryPos.get(
        `api/v1/TblDmInventory/get-select-branch?auth=true`,
      );
      if (response && response.success) {
        const transformedData = response.data
          .filter((i: any) => i.att2 && i.text)
          .map((item: any) => {
            return {
              value: item.att2,
              label: item.att2 + " - " + item.text,
              id: item.value,
            };
          });

        setDataBranchCode(transformedData);
      }
    } catch (error) {
      console.error(error);
      return;
    }
  };

  const handleCleanKeySearch = (str: string) => {
    if (typeof str !== "string") {
      return "";
    }

    let cleaned = str.replace(/;+/g, ";").replace(/;\s*$/, "");
    return cleaned;
  };

  //Mã vật tư
  const getDataItem = async () => {
    try {
      let url = "";

      if (
        handleCleanKeySearch(searchItem ?? "").length > 1 &&
        handleCleanKeySearch(searchItem ?? "")
          .trim()
          .includes(";")
      ) {
        const cleanKeySearch = handleCleanKeySearch(searchItem ?? "")
          ?.toUpperCase()
          ?.trim();

        url += `${cleanKeySearch
          ?.split(";")
          ?.map((item) => `&ListItemCode=${item.trim().replace(" ", "")}`)
          .join("")}`;
      } else if (
        handleCleanKeySearch(searchItem ?? "")?.includes(";") === false &&
        (searchItem ?? "")?.length > 0
      ) {
        url += `&ListItemCode=${searchItem?.toUpperCase()}`;
      }

      const response = await repositoryPos.get(
        `api/v1/TblItem/get-list?Skip=0&Take=100` + url,
      );
      if (response && response.success) {
        const transformedData = response.data
          ?.filter((item: any) => item.itemCode && item.itemName)
          .map((item: any) => ({
            label: item.itemCode + " - " + item.itemName,
            value: item.itemId?.toString(),
            code: item.itemCode,
          }));
        const uniqueData = Array.from(
          new Set(transformedData.map((item: any) => item.value)),
        ).map((value) =>
          transformedData.find((item: any) => item.value === value),
        );
        setDataItem(uniqueData);
      }
    } catch (error) {
      console.error(error);
      return;
    }
  };
  // Memoize the menu items
  const menuItems = useMemo(() => {
    if (dataItem.length === 0) {
      return (
        <Menu.Item>
          <Flex justify="center">
            <Text size="sm">
              {searchItem.length > 0
                ? "Không tìm thấy dữ liệu !!!"
                : "Nhập mã vật tư để tìm kiếm !!!"}
            </Text>
          </Flex>
        </Menu.Item>
      );
    }

    return dataItem.map((item, index) => (
      <Menu.Item
        key={`${item.value}-${index}`} // Better key
        onClick={() => {
          const updatedItemCode = searchItem
            ?.toUpperCase()
            .includes(item.code?.toUpperCase())
            ? searchItem
                .toUpperCase()
                .split(";")
                .filter((val) => val.trim() !== item.code.toUpperCase())
                .join(";")
            : searchItem === ""
              ? item.code
              : `${searchItem};${item.code}`;
          form.setFieldValue("VatTuCode", updatedItemCode);
          setSearchItem(updatedItemCode);
        }}
      >
        <Flex align="center" gap="xs">
          {searchItem.toUpperCase().includes(item.code?.toUpperCase()) && (
            <IconCheck size={14} color="#808080" />
          )}
          <Text size="sm" truncate="end">
            {item.label}
          </Text>
        </Flex>
      </Menu.Item>
    ));
  }, [dataItem, searchItem]);

  // ExportData Excel
  const exportDataExcel = async () => {
    open();
    try {
      let params = new URLSearchParams();
      const values = form.getValues();

      if (values.keySearch) {
        params.append("KeySearch", values.keySearch.trim());
      }
      if (values.MaChiNhanh && values.MaChiNhanh.length > 0) {
        values.MaChiNhanh.forEach((branchCode: string) => {
          params.append("MaChiNhanh", branchCode);
        });
      }
      if (values.MaKho && values.MaKho.length > 0) {
        values.MaKho.forEach((invCode: string) => {
          params.append("MaKho", invCode);
        });
      }
      if (values.VatTuCode) {
        values.VatTuCode.split(";")
          .map((s) => s.trim())
          .filter(Boolean)
          .forEach((maVatTu: string) => {
            params.append("MaVatTu", maVatTu);
          });
      }
      if (values.TuNgay) {
        params.append("TuNgay", values.TuNgay);
      }
      if (values.DenNgay) {
        params.append("DenNgay", values.DenNgay);
      }

      const url = `${BASE_API_MDM}/api/v1/BcThoiHanMaThekPhieuBhg/export-bao-cao-thoi-han-ma-thek-phieu-bhg?isAll=true${
        params.toString() ? `&${params.toString()}` : ""
      }`;
      const token = localStorage.getItem("token")?.replace(/"/g, "");

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/vnd.ms-excel",
        },
      });

      if (!response.ok) throw new Error("Failed to dowload file");

      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = "bao-cao-thoi-han-ma-thek-phieu-bhg.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error("Error:", error);
      NotificationExtension.Fails("Xuất Excel thất bại");
    } finally {
      close();
    }
  };

  useEffect(() => {
    if (debouncedItemCode.length > 0) {
      getDataItem();
    } else {
      setDataItem([]);
    }
  }, [debouncedItemCode]);
  useEffect(() => {
    DataBrachCode();
    DataInvCode();
  }, []);
  return (
    <Box pos={"relative"} style={containerStyle}>
      <LoadingOverlayCustom visible={visible} />
      <div
        style={{
          border: "1px solid #dee2e6",
          padding: "10px",
          overflowX: "hidden",
        }}
      >
        <Group justify="space-between" gap={0}>
          <BreadcrumbLayout1 />
          <Button
            leftSection={
              <IconFileExport style={{ width: rem(14), height: rem(14) }} />
            }
            onClick={exportDataExcel}
          >
            Export Excel
          </Button>
        </Group>
        <Grid mt={10}>
          <Grid.Col span={sizeGrid}>
            <Input
              placeholder="Nhập tìm kiếm"
              type="text"
              leftSection={<IconSearch size={20} color="#15aabf" />}
              value={form.getValues().keySearch}
              onChange={(e) => form.setFieldValue("keySearch", e.target.value)}
              onKeyDown={async (e) => {
                if (e.key === "Enter") handleSearch();
              }}
            />
          </Grid.Col>
          <Grid.Col span={sizeGrid}>
            <MultiSelect
              placeholder="Chi nhánh"
              clearable
              searchable
              data={dataBranchCode}
              value={form.getValues().MaChiNhanh}
              onChange={(v) => {
                form.setFieldValue("MaChiNhanh", v);
                DataInvCode();
              }}
              leftSection={<IconBuildingSkyscraper size={20} color="#15aabf" />}
            />
          </Grid.Col>
          <Grid.Col span={sizeGrid}>
            <MultiSelect
              size="sm"
              placeholder="Mã kho"
              searchable
              clearable
              data={dataInvCode}
              value={form.getValues().MaKho}
              onChange={(v) => form.setFieldValue("MaKho", v)}
              leftSection={<IconBuildingWarehouse size={20} color="#15aabf" />}
            />
          </Grid.Col>
          <Grid.Col span={sizeGrid}>
            <Box w={"100%"} style={{ position: "relative" }}>
              <Menu
                width="target"
                position="bottom"
                offset={5}
                keepMounted
                closeOnItemClick={false}
              >
                <Menu.Target>
                  <TextInput
                    placeholder="Nhập mã vật tư"
                    leftSection={<IconBarcode size={20} color="#15aabf" />}
                    rightSectionPointerEvents="all"
                    rightSection={
                      <IconX
                        size={20}
                        color="#15aabf"
                        onClick={() => {
                          setSearchItem("");
                          form.setFieldValue("VatTuCode", "");
                        }}
                        style={{
                          display: searchItem.length > 0 ? undefined : "none",
                          cursor: "pointer",
                        }}
                      />
                    }
                    value={searchItem}
                    onChange={(e) => {
                      setSearchItem(e.currentTarget.value);
                      form.setFieldValue("VatTuCode", e.currentTarget.value);
                    }}
                    style={{ width: "100%" }}
                  />
                </Menu.Target>
                <Menu.Dropdown>
                  <ScrollArea
                    h={
                      dataItem.length <= 1
                        ? 32
                        : dataItem.length === 2
                          ? 64
                          : dataItem.length === 3
                            ? 96
                            : dataItem.length === 4
                              ? 128
                              : dataItem.length === 5
                                ? 160
                                : dataItem.length === 6
                                  ? 192
                                  : 225
                    }
                    offsetScrollbars
                    scrollbars="y"
                  >
                    {menuItems}
                  </ScrollArea>
                </Menu.Dropdown>
              </Menu>
            </Box>
          </Grid.Col>
          <Grid.Col span={sizeGrid}>
            <DatePickerInput
              size="sm"
              placeholder="Chọn ngày bắt đầu"
              locale="vi"
              valueFormat="DD/MM/YYYY"
              leftSection={<IconTimeDuration15 size={20} color="#15aabf" />}
              key={form.key("TuNgay")}
              {...form.getInputProps("TuNgay")}
              value={
                form.getValues().TuNgay
                  ? dayjs(form.getValues().TuNgay, "YYYY-MM-DD").toDate()
                  : null
              }
              onChange={(date) =>
                form.setFieldValue(
                  "TuNgay",
                  date ? dayjs(date).format("YYYY-MM-DD") : null,
                )
              }
              clearable
            />
          </Grid.Col>
          <Grid.Col span={sizeGrid}>
            <DatePickerInput
              size="sm"
              placeholder="Chọn ngày kết thúc"
              locale="vi"
              valueFormat="DD/MM/YYYY"
              leftSection={<IconTimeDuration15 size={20} color="#15aabf" />}
              key={form.key("DenNgay")}
              {...form.getInputProps("DenNgay")}
              value={
                form.getValues().DenNgay
                  ? dayjs(form.getValues().DenNgay, "YYYY-MM-DD").toDate()
                  : null
              }
              onChange={(date) =>
                form.setFieldValue(
                  "DenNgay",
                  date ? dayjs(date).format("YYYY-MM-DD") : null,
                )
              }
              clearable
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 6, lg: 0.9 }}>
            <Button color="blue" variant="outline" onClick={handleSearch}>
              Tìm kiếm
            </Button>
          </Grid.Col>
        </Grid>
      </div>
      <div style={gridStyle} className={"ag-theme-quartz"}>
        <AgGridReact<any>
          ref={gridRef}
          loading={loading}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowModelType={"serverSide"}
          pagination={true}
          paginationPageSize={100}
          paginationPageSizeSelector={paginationPageSizeSelector}
          onSortChanged={handleSortChanged}
          onGridReady={onGridReady}
          getRowId={getRowId}
          getRowStyle={getRowStyle}
          localeText={AG_GRID_LOCALE_VN}
        />
      </div>
    </Box>
  );
};
export default GetTHEKPHG;
